import { useState, useEffect, useMemo } from 'react';
import { systemConfigAPI } from '../services/api';
import { buildHolidayDateSet } from '../utils/billAmountPreview';

let _cachedConfig = null;
let _fetchPromise = null;

/**
 * Fetches tariff configuration (taxes au poids + TS options + TVA rate) from the server.
 * A module-level cache ensures only one request is made per page load.
 */
export const useTarifConfig = () => {
  const [tarifConfig, setTarifConfig] = useState(_cachedConfig);
  const [loading, setLoading] = useState(_cachedConfig === null);

  useEffect(() => {
    if (_cachedConfig !== null) {
      setTarifConfig(_cachedConfig);
      setLoading(false);
      return;
    }

    if (!_fetchPromise) {
      _fetchPromise = systemConfigAPI.getTariffs()
        .then((res) => {
          _cachedConfig = res?.data || null;
          return _cachedConfig;
        })
        .catch(() => {
          _fetchPromise = null; // allow retry on error
          return null;
        });
    }

    _fetchPromise.then((config) => {
      setTarifConfig(config);
      setLoading(false);
    });
  }, []);

  const holidayDateSet = useMemo(
    () => buildHolidayDateSet(tarifConfig?.holidayDateKeys),
    [tarifConfig?.holidayDateKeys]
  );

  return { tarifConfig, holidayDateSet, loading };
};

/**
 * Map server TS options (code+description+hourlyNetAmount) to the select option shape
 * expected by <Select> components: { value, label }.
 */
export const mapTsOptionsForSelect = (tsOptions) => {
  if (!Array.isArray(tsOptions) || tsOptions.length === 0) return null;
  return tsOptions.map((opt) => ({
    value: opt.code || opt.value,
    label: `${opt.description} (${Number(opt.hourlyNetAmount).toLocaleString('fr-FR')} FCFA/h)`
  }));
};
